/* ============================================================
   LAB EFFECTS NAVBAR — interactions (Horizon port)
   The menu markup is rendered by Liquid blocks; this script only
   wires up behavior: promo collapse, scroll tint, click-to-open
   mega menus, scrim, scroll-lock, and the mobile off-canvas menu
   (built by cloning the desktop nav so there is one source of
   truth for menu content).
   ============================================================ */
(function () {
  'use strict';

  function initRoot(root) {
    if (!root || root.dataset.leInit === 'true') return;
    root.dataset.leInit = 'true';

    const topbar = root.querySelector('.le-topbar');
    const navbar = root.querySelector('.le-navbar');
    const scrim = root.querySelector('.le-nav-scrim');
    const primary = root.querySelector('.le-nav-primary');
    const items = primary ? Array.from(primary.querySelectorAll('.le-nav-item')) : [];
    const hamburger = root.querySelector('.le-hamburger');
    const mobileMenu = root.querySelector('.le-mobile-menu');
    const mobileNav = root.querySelector('.le-mobile-nav');
    const promoClose = root.querySelector('.le-promo__close');

    const caretSVG = '<svg class="le-caret" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M11 5.5 7 9.5 3 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    let promoDismissed = false;

    /* ---------- keep global header height vars in sync ---------- */
    function updateHeaderVars() {
      if (navbar) {
        document.body.style.setProperty('--header-height', navbar.offsetHeight + 'px');
      }
      if (topbar) {
        document.body.style.setProperty('--header-group-height', topbar.offsetHeight + 'px');
      }
    }

    /* ---------- scroll lock helper (count-based) ---------- */
    // The DESKTOP MEGA MENU never locks the page. It is not a modal: its scrim
    // sits at z-index 40, BELOW the navbar's z-index 100, so the nav stays
    // visible and clickable and the page behind stays scrollable. Locking it
    // also froze the page under a blurred scrim with no nav in sight.
    //
    // Only the MOBILE off-canvas menu locks -- it is a full-screen overlay
    // (position:fixed; inset:0; z-index 45) and its own nav lives inside it.
    // It goes through the shared lock so it can never fight an open slide-up.
    var navHoldsLock = false;
    function setScrollLock(locked) {
      if (locked === navHoldsLock) return;      // never double-count
      navHoldsLock = locked;
      if (!window.LESheetLock) return;
      if (locked) LESheetLock.lock(); else LESheetLock.unlock();
    }

    /* ---------- desktop mega open / close ---------- */
    function anyOpen() { return items.some((i) => i.classList.contains('le-open')); }

    function syncScrim() {
      const open = anyOpen();
      if (scrim) scrim.classList.toggle('le-active', open);
      // NOTE: `open` (desktop mega menu) is deliberately NOT part of this --
      // see setScrollLock above. Only the mobile drawer locks the page.
      setScrollLock(!!(mobileMenu && mobileMenu.classList.contains('le-open')));
    }

    function closeAll() {
      items.forEach((i) => {
        i.classList.remove('le-open');
        const link = i.querySelector('.le-nav-link');
        if (link && link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
      });
      syncScrim();
    }

    items.forEach((item) => {
      const link = item.querySelector('.le-nav-link');
      const mega = item.querySelector('.le-mega');
      if (!link) return;

      if (mega) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const willOpen = !item.classList.contains('le-open');
          closeAll();
          if (willOpen) {
            item.classList.add('le-open');
            link.setAttribute('aria-expanded', 'true');
          }
          syncScrim();
        });
        // Only keep the menu open for clicks on the actual panel. The .le-mega
        // wrapper spans the full navbar width, so clicks in the empty margins
        // beside a centered panel must still bubble to the document closer.
        mega.addEventListener('click', (e) => {
          if (e.target.closest('.le-shop-inner, .le-mega-panel')) e.stopPropagation();
        });
      } else {
        link.addEventListener('click', () => closeAll());
      }
    });

    /* ---------- shop mega: rail hover/click swaps the active pane ----------
       Bound to the ORIGINAL desktop rail only (inside .le-nav-primary). The
       mobile drawer gets a fresh clone with its own accordion behavior, so the
       two never conflict. */
    const shopInner = primary ? primary.querySelector('.le-mega--shop .le-shop-inner') : null;
    if (shopInner) {
      const shopCats = Array.from(shopInner.querySelectorAll('.le-shop-cat'));
      const activateCat = (cat) => {
        shopCats.forEach((c) => {
          const on = c === cat;
          c.classList.toggle('is-active', on);
          const rail = c.querySelector('.le-shop-rail-item');
          if (rail) rail.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
      };
      shopCats.forEach((cat) => {
        const rail = cat.querySelector('.le-shop-rail-item');
        if (!rail) return;
        rail.addEventListener('mouseenter', () => activateCat(cat));
        rail.addEventListener('click', (e) => { e.preventDefault(); activateCat(cat); });
      });
      if (shopCats[0]) activateCat(shopCats[0]);
    }

    if (scrim) scrim.addEventListener('click', closeAll);
    document.addEventListener('click', closeAll);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeAll();
        closeMobile();
      }
    });

    /* ---------- promo dismiss ---------- */
    if (promoClose && topbar) {
      promoClose.addEventListener('click', () => {
        topbar.classList.add('le-promo-hidden');
        promoDismissed = true;
      });
    }

    /* ---------- scroll behavior ----------
       Promo collapse is driven by ABSOLUTE scroll position with hysteresis, not
       by scroll direction. Direction-based logic fights the layout shift caused
       by the promo collapsing (the height change reads as a reverse scroll),
       which produces jitter at slow speeds. Position + hysteresis is stable at
       any scroll speed. */
    const PROMO_HIDE_AT = 60; // once scrolled past this, roll the promo up
    const PROMO_SHOW_AT = 4;  // only reveal it again near the very top

    /* While any overlay has the page scroll-locked, FREEZE the promo/tint
       state instead of recomputing it.
       Overlays lock the page in more than one way, and every one of them
       makes window.scrollY read 0 while the reader is actually scrolled down
       (see isScrollLocked below for the three mechanisms). A scroll event
       fires on the way in and out. onScroll then saw y=0 < PROMO_SHOW_AT and rolled the promo
       bar back DOWN behind the open drawer, then back up on close -- a
       distracting slide every time the cart or search was used while scrolled.
       (The account popover never did this: its dialog carries no scroll-lock.)
       Bailing out keeps whatever state the promo was already in, which is
       exactly right at the top of the page AND scrolled down. */
    function isScrollLocked() {
      const h = document.documentElement;

      // (a) our own slide-ups (LESheetLock), and Horizon's `scroll-lock`
      //     attribute -- note that attribute path only ever fires for
      //     <details scroll-lock>, NOT for the cart drawer or search modal.
      if (h.hasAttribute('scroll-lock') || h.classList.contains('le-sheet-locked')) return true;

      // (b) THE CART DRAWER AND SEARCH MODAL. dialog.js showDialog() locks by
      //     pinning the body: `width:100%; position:fixed; top:-<scrollY>px`.
      //     While body is fixed the document is not scrolled at all -- the
      //     offset is faked with `top` -- so window.scrollY reports 0 no matter
      //     how far down the reader actually is. That is what dropped the promo
      //     bar back into view behind the open drawer. Read the inline style,
      //     which is exactly what dialog.js writes.
      if (document.body.style.position === 'fixed') return true;

      // (c) belt and braces: anything opened with showModal() blocks page
      //     scrolling at the browser level. :modal is unsupported in older
      //     browsers, hence the try/catch.
      try { return !!document.querySelector('dialog:modal'); } catch (e) { return false; }
    }

    function onScroll() {
      if (isScrollLocked()) return;
      const y = window.scrollY;
      if (navbar) navbar.classList.toggle('le-scrolled', y > 8);
      if (topbar && !promoDismissed) {
        if (y > PROMO_HIDE_AT) {
          topbar.classList.add('le-promo-hidden');
        } else if (y < PROMO_SHOW_AT) {
          topbar.classList.remove('le-promo-hidden');
        }
        /* between the two thresholds: leave state unchanged (hysteresis) */
      }
      updateHeaderVars();
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateHeaderVars, { passive: true });
    onScroll();
    updateHeaderVars();

    /* ---------- eased single-open accordion engine ----------
       Animates a body's max-height + opacity. `accItems` is a group of
       accordion items; only one stays open at a time within the group.
       Reused for the top-level drawer items and the shop's nested
       category accordions so every level animates identically. */
    function easedAccordion(accItems, headSel, bodySel, openClass) {
      const DUR = 340;
      const ease = 'cubic-bezier(0.16, 1, 0.3, 1)';

      /* Finish on transitionend OR on a timer, whichever lands first.
         THIS IS THE "SHOP ALL OPENS EMPTY" FIX. Both halves used to hang their
         cleanup off `transitionend` alone, which is not guaranteed to fire:
         a max-height that does not actually change (0px -> 0px) starts no
         transition and therefore dispatches no event, and neither does a
         transition on a backgrounded tab or under prefers-reduced-motion.
         When that happened during expand(), max-height was left pinned at
         `0px` and overflow at `hidden` — the item carried the open class and
         its caret flipped, but the panel had no height, so the menu looked
         empty. It also latched: the next click ran collapse(), which is
         another 0 -> 0 no-op, so the open class never came off. That is the
         intermittent, unreproducible empty menu.
         The timer guarantees cleanup runs exactly once either way. */
      function settle(body, fn) {
        let fired = false;
        let timer = 0;
        const finish = (e) => {
          if (e && (e.target !== body || e.propertyName !== 'max-height')) return;
          if (fired) return;
          fired = true;
          clearTimeout(timer);
          body.removeEventListener('transitionend', finish);
          fn();
        };
        timer = setTimeout(finish, DUR + 120);
        body.addEventListener('transitionend', finish);
      }

      function expand(item) {
        const body = item.querySelector(bodySel);
        if (!body) return;
        item.classList.add(openClass);
        body.style.overflow = 'hidden';
        body.style.opacity = '0';
        body.style.maxHeight = '0px';
        void body.offsetHeight;
        /* Measure AFTER the reset reflow. A zero here means the content has no
           laid-out height yet (cloned panel, images still resolving); animating
           to 0px would leave the panel permanently shut, so skip straight to
           the open state and let it size itself. */
        const target = body.scrollHeight;
        const openNow = () => {
          body.style.maxHeight = 'none';
          body.style.opacity = '1';
          body.style.transition = '';
          body.style.overflow = '';
        };
        if (!target) { openNow(); return; }
        body.style.transition = 'max-height ' + DUR + 'ms ' + ease + ', opacity ' + DUR + 'ms ' + ease;
        body.style.maxHeight = target + 'px';
        body.style.opacity = '1';
        settle(body, openNow);
      }
      function collapse(item) {
        const body = item.querySelector(bodySel);
        if (!body) return;
        const closeNow = () => {
          item.classList.remove(openClass);
          body.style.transition = '';
        };
        body.style.overflow = 'hidden';
        body.style.maxHeight = body.scrollHeight + 'px';
        body.style.opacity = '1';
        void body.offsetHeight;
        body.style.transition = 'max-height ' + DUR + 'ms ' + ease + ', opacity ' + DUR + 'ms ' + ease;
        body.style.maxHeight = '0px';
        body.style.opacity = '0';
        settle(body, closeNow);
      }
      accItems.forEach((item) => {
        const head = item.querySelector(headSel);
        if (!head) return;
        head.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item.classList.contains(openClass)) { collapse(item); return; }
          accItems.forEach((o) => { if (o !== item && o.classList.contains(openClass)) collapse(o); });
          expand(item);
        });
      });
    }

    /* ---------- build mobile menu from desktop nav ----------
       One source of truth: each desktop nav item is cloned into the drawer.
       The Shop All mega clones its 3-zone .le-shop-inner (which restyles to a
       category accordion via CSS); other megas clone their .le-mega-panel. */
    function buildMobile() {
      if (!mobileNav) return;
      mobileNav.innerHTML = '';
      const shopGroups = [];
      items.forEach((item) => {
        const link = item.querySelector('.le-nav-link');
        if (!link) return;
        const labelEl = link.querySelector('.le-nav-link-label');
        const label = labelEl ? labelEl.innerHTML : link.textContent.trim();
        const isNew = !!link.querySelector('.le-new-badge');
        const newBadge = isNew ? ' <span class="le-new-badge">New</span>' : '';

        const shopSrc = item.querySelector('.le-mega--shop .le-shop-inner');
        const source = shopSrc || item.querySelector('.le-mega .le-mega-panel');

        if (source) {
          const acc = document.createElement('div');
          acc.className = 'le-acc';
          const head = document.createElement('button');
          head.type = 'button';
          head.className = 'le-acc-head';
          head.innerHTML = '<span class="le-acc-label">' + label + newBadge + '</span>' + caretSVG;
          const body = document.createElement('div');
          body.className = 'le-acc-body';
          const clone = source.cloneNode(true);
          body.appendChild(clone);
          acc.appendChild(head);
          acc.appendChild(body);
          mobileNav.appendChild(acc);
          if (shopSrc) {
            const cats = Array.from(clone.querySelectorAll('.le-shop-cat'));
            cats.forEach((c) => c.classList.remove('is-active'));
            shopGroups.push(cats);
          }
        } else {
          const a = document.createElement('a');
          a.className = 'le-m-link';
          a.href = link.getAttribute('href') || '#';
          a.innerHTML = '<span class="le-acc-label">' + label + newBadge + '</span>';
          mobileNav.appendChild(a);
        }
      });

      const topItems = Array.from(mobileNav.children).filter((c) => c.classList.contains('le-acc'));
      easedAccordion(topItems, '.le-acc-head', '.le-acc-body', 'le-open');
      shopGroups.forEach((cats) => easedAccordion(cats, '.le-shop-rail-item', '.le-shop-stage', 'is-open'));
    }
    buildMobile();

    /* ---------- mobile open / close ---------- */
    function closeMobile() {
      if (!mobileMenu) return;
      mobileMenu.classList.remove('le-open');
      if (hamburger) hamburger.classList.remove('le-active');
      if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
      syncScrim();
    }
    if (hamburger && mobileMenu) {
      hamburger.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = mobileMenu.classList.toggle('le-open');
        hamburger.classList.toggle('le-active', open);
        hamburger.setAttribute('aria-expanded', open ? 'true' : 'false');
        syncScrim();
      });
    }
  }

  function init() {
    document.querySelectorAll('.le-root').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-init in the theme editor when sections are re-rendered
  document.addEventListener('shopify:section:load', init);
})();
